// docs/30 in the encounter HUD: every foe has a mind — its people, how clever it is, its mood,
// its quirks, what it carries and the cards in its tactics deck. The foe list shows the card
// behind each declared intent and a mood mark; clicking a foe opens this card beside its row.
// Every word is the sim's (`MindNote`); this file only lays them out.
import { createPortal } from 'react-dom';
import type { FocusNote, IntentNote, MindNote, NoteLine } from '../../core/sim/types';
import { MindFog } from './Fog';

/** A mark beside a foe's name for any mood but Steady. */
export const MOOD_MARK: Record<string, string> = { Bold: '▲', Shaken: '≈', Enraged: '✸' };

export function MoodMark({ mood }: { mood: NoteLine }) {
  const mark = MOOD_MARK[mood.name];
  if (!mark) return null;
  return <span className={`fk-mind__mood fk-mind__mood--${mood.name.toLowerCase()}`} title={`${mood.name}: ${mood.line}`} aria-label={mood.name}>{mark}</span>;
}

/**
 * The tactics card behind a declared intent: a small tag, the rule on hover. While the foe holds
 * to the card for a spell (docs/32), pips show how far into it this pulse is (●●○ = 2 of 3).
 */
export function TacticTag({ tactic, spell }: { tactic: NoteLine | undefined; spell?: IntentNote }) {
  if (!tactic) return null;
  const held = spell && spell.card === tactic.name && spell.of > 1 ? spell : undefined;
  const title = held ? `${tactic.line} — turn ${held.pulse} of ${held.of}: ${held.hold}.` : tactic.line;
  return (
    <span className="fk-mind__tactic" title={title} data-testid="foe-tactic">
      {tactic.name}
      {held && (
        <span className="fk-mind__spell" aria-label={`turn ${held.pulse} of ${held.of}`} data-testid="foe-spell">
          {Array.from({ length: held.of }, (_, k) => (k < held.pulse ? '●' : '○')).join('')}
        </span>
      )}
    </span>
  );
}

const signed = (v: number) => (v > 0 ? `+${v}` : `${v}`);

/** Its focus (docs/32): how long it holds to a card and what makes it so (not what it holds to: docs/46 T2). */
function MindFocus({ focus }: { focus: FocusNote | undefined }) {
  if (!focus) return null;
  return (
    <div className="fk-mind__focus" data-testid="mind-focus">
      <p className="fk-mind__line">
        <span className="fk-hud__label">Focus</span> <b className="fk-mind__focus-score">{focus.score}</b>/10 · <b title={focus.line}>{focus.name}</b>
      </p>
      <p className="fk-mind__focus-parts">
        {focus.parts.map((p) => (
          <span key={p.name} className={`fk-mind__part ${p.value < 0 ? 'fk-mind__part--down' : ''}`}>{p.name} {signed(p.value)}</span>
        ))}
      </p>
      {/* docs/46 T2: what card it holds to, and at whom, is its plan — the party does not see it */}
      {focus.lapse && <p className="fk-mind__line fk-muted" title={focus.lapse.line}>Last broke off: <b>{focus.lapse.name}</b> — {focus.lapse.line}</p>}
    </div>
  );
}

function Chips({ items, kind }: { items: readonly NoteLine[]; kind: string }) {
  if (!items.length) return null;
  return (
    <span className="fk-mind__chips">
      {items.map((q) => <span key={q.name} className={`fk-mind__chip fk-mind__chip--${kind}`} title={q.line} tabIndex={0}>{q.name}</span>)}
    </span>
  );
}

/**
 * The inspect card: who this foe is and what its deck holds. Portalled to `<body>` and placed
 * beside its row (`anchor`), because the HUD's panels clip what spills out of them.
 */
export function MindCard({ mind, anchor }: { mind: MindNote; anchor: DOMRect | undefined }) {
  const total = mind.deck.reduce((s, c) => s + c.count, 0);
  const style = anchor
    ? { top: Math.max(8, Math.min(anchor.top - 4, window.innerHeight - 420)), right: Math.max(8, window.innerWidth - anchor.left + 12) }
    : undefined;
  return createPortal(
    <div className="fk-mind fk-hit" style={style} data-testid="mind-card" data-unit-id={mind.unit}>
      <div className="fk-mind__head">
        <span className="fk-mind__kin" title={mind.doctrine}>{mind.kin}</span>
        <span className="fk-mind__wits" title={mind.wits.line}>{mind.wits.name}</span>
        <span className={`fk-mind__moodname fk-mind__moodname--${mind.mood.name.toLowerCase()}`} title={mind.mood.line}>{mind.mood.name}</span>
      </div>
      <p className="fk-mind__line">{mind.doctrine}</p>
      <p className="fk-mind__line fk-muted">Prefers {mind.prefers} · nerve {mind.nerve}/5{mind.last ? <> · last played <b title={mind.last.line}>{mind.last.name}</b></> : null}</p>
      {(mind.quirks.length > 0 || mind.gear.length > 0) && (
        <div className="fk-mind__row"><Chips items={mind.quirks} kind="quirk" /><Chips items={mind.gear} kind="gear" /></div>
      )}
      <MindFocus focus={mind.focus} />
      <MindLore mind={mind} />
      <MindFog mind={mind} />
      <div className="fk-mind__deck" aria-label={`Tactics deck, ${total} cards`}>
        <span className="fk-hud__label">Deck · {total} <span className="fk-muted">({mind.drawLeft} to draw)</span></span>
        <ul>
          {mind.deck.map((c) => (
            <li key={c.name} className={`fk-mind__card fk-mind__card--${c.family}`} title={`${c.line} (from ${c.from})`} tabIndex={0}>
              <span className="fk-mind__card-name">{c.name}</span>
              <span className="fk-mind__card-count">×{c.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

/** docs/115: how keenly it reads the field, its part in a scheme, what it has learned of you. */
function MindLore({ mind }: { mind: MindNote }) {
  if (!mind.insight && !mind.part && !mind.knows.length) return null;
  return (
    <div className="fk-mind__lore" data-testid="mind-lore">
      {mind.insight && <p className="fk-mind__line fk-muted" title={mind.insight.line}>Insight <b>{mind.insight.name}</b> · {mind.insight.line}</p>}
      {mind.part && <p className="fk-mind__line" title={mind.part.line}><b>{mind.part.name}</b> · {mind.part.line}</p>}
      {mind.knows.length > 0 && <span className="fk-hud__label">What it knows of you</span>}
      {mind.knows.map((k) => <p key={k.name} className="fk-mind__line"><b>{k.name}</b>: {k.line}</p>)}
    </div>
  );
}

/** The fight's situations (docs/30 Q7), once, under the foe list. */
export function Situations({ minds }: { minds: readonly MindNote[] | undefined }) {
  const s = minds?.[0]?.situations ?? [];
  if (!s.length) return null;
  return (
    <p className="fk-mind__situations" data-testid="situations">
      {s.map((x) => <span key={x.name} className="fk-mind__chip fk-mind__chip--situation" title={x.line} tabIndex={0}>{x.name}</span>)}
    </p>
  );
}
